import React, { memo, Fragment, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';

import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';

import styles from 'components/ProductList/ProductList.scss';

import { useWtrSelector } from 'redux/hooks';
import { getScrolledLineNumber } from 'redux/modules/scroll-session-storage/selectors';

type setAutoFocusIndexCallback = (autoFocusIndex?: number) => void;

interface ProductListProps {
  children?: React.ReactNode;
  contentComponent?: boolean;
  displayInGroups?: boolean;
  withContainer?: boolean;
  id?: string;
  InfiniteScroll?: React.ComponentType<React.PropsWithChildren> | null;
  loading?: boolean;
  LoadMoreButton?: React.ComponentType<{
    setAutoFocusIndex?: setAutoFocusIndexCallback;
  }> | null;
  setAutoFocusIndex?: setAutoFocusIndexCallback;
}

const ProductList = ({
  children = null,
  contentComponent = false,
  displayInGroups = false,
  withContainer = true,
  id = 'tSr',
  InfiniteScroll = null,
  loading = false,
  LoadMoreButton = null,
  setAutoFocusIndex,
}: ProductListProps) => {
  const scrolledLineNumber = useWtrSelector(getScrolledLineNumber);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (scrolledLineNumber) {
      const productPodEl = document.querySelector(`[data-product-id="${scrolledLineNumber}"]`);
      productPodEl?.scrollIntoView();
    }
  }, [loading, scrolledLineNumber]);

  if (!loading && (!children || React.Children.count(children) < 1)) {
    return null;
  }

  const list = (
    <div className={classNames(styles.flexGrid, { [styles.inGroups]: displayInGroups })}>
      {children}
    </div>
  );

  const listWithLoadMore = InfiniteScroll ? (
    <InfiniteScroll>{list}</InfiniteScroll>
  ) : (
    <Fragment>
      {list}
      {LoadMoreButton && <LoadMoreButton setAutoFocusIndex={setAutoFocusIndex} />}
    </Fragment>
  );

  return (
    <>
      <Helmet>
        <link
          rel="preconnect"
          href="https://ecom-su-static-prod.wtrecom.com"
          data-testid="link-preconnect"
        />
      </Helmet>
      <div className={classNames('container-fluid', { [styles.spinnerWrapper]: loading })}>
        <Spinner ariaTextInactive="Loading finished" isActive={loading} />
      </div>
      {!loading && (
        <div
          className={classNames(styles.productList, {
            [styles.contentComponent]: contentComponent,
          })}
          data-testid="product-list"
          id={id}
        >
          <div
            className={classNames({
              'container-fluid': withContainer,
            })}
          >
            {listWithLoadMore}
          </div>
        </div>
      )}
    </>
  );
};

ProductList.displayName = 'ProductList';

export default memo(ProductList);
